import * as React from "react"
import Image from "next/image"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/**
 * 認証・公開画面（login / signup / forgot-password / reset-password /
 * onboarding / invite / unauthorized）で共通利用する視覚レイヤー。
 * ロジックは持たない純粋なプレゼンテーション部品のみ。
 */

export function AuthLayout({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-muted/40 px-4 py-10 dark:bg-muted/10 sm:py-14">
      <div className={cn("relative w-full max-w-md", className)}>{children}</div>
    </div>
  )
}

export function AuthBrand({ className }: { className?: string }) {
  return (
    <div className={cn("mb-6 flex flex-col items-center gap-3", className)}>
      <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <Image src="/brand/p-mark.png" alt="" width={56} height={56} priority />
      </span>
      <div className="flex flex-col items-center gap-1.5 text-center">
        <Image src="/brand/logo.webp" alt="Plate One" width={132} height={22} className="object-contain dark:invert" priority />
        <p className="text-xs text-muted-foreground">飲食店のためのシフト管理クラウド</p>
      </div>
    </div>
  )
}

export function AuthDivider({ label = "または" }: { label?: string }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t border-border" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-card px-2 text-muted-foreground">{label}</span>
      </div>
    </div>
  )
}

export function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

export function GoogleAuthButton({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="h-11 w-full gap-3 font-medium"
      onClick={onClick}
    >
      <GoogleIcon />
      {children}
    </Button>
  )
}

/**
 * invite / unauthorized / 無効リンクなどの「状態表示」用センターカード。
 * 大きめアイコン + タイトル + 説明 + CTA ボタン群。
 */
export function AuthStatePanel({
  icon,
  tone = "info",
  title,
  description,
  children,
}: {
  icon: React.ReactNode
  tone?: "info" | "success" | "danger"
  title: string
  description?: React.ReactNode
  children?: React.ReactNode
}) {
  const toneClasses = {
    info: "bg-muted text-foreground",
    success: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300",
    danger: "bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-300",
  } as const

  return (
    <div className="flex flex-col items-center px-6 py-10 text-center sm:px-10">
      <span
        className={cn(
          "flex h-16 w-16 items-center justify-center rounded-full [&>svg]:h-8 [&>svg]:w-8",
          toneClasses[tone],
        )}
        aria-hidden
      >
        {icon}
      </span>
      <h1 className="mt-5 text-xl font-semibold text-foreground">{title}</h1>
      {description && (
        <div className="mt-2 text-sm leading-6 text-muted-foreground">{description}</div>
      )}
      {children && (
        <div className="mt-6 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">{children}</div>
      )}
    </div>
  )
}
